import Head from 'next/head';
import React from 'react';

import { getStructuredData } from '~/common/utils/seo';

const constructStructuredData = ({
  currencyCode,
  maxPrice,
  minPrice,
  offerCount,
  ownersCount,
  ratingCount,
  ratingValue,
  serviceType,
  stateName,
  suburb,
  url,
}: SeoStructuredData): string => {
  const breadcrumbData = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home page',
        item: 'https://www.madpaws.com.au/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: serviceType,
        item: `https://www.madpaws.com.au${url}`,
      },
    ],
  };

  const roundedRoundedRating = ratingValue
    ? Math.round((ratingValue + Number.EPSILON) * 100) / 100
    : 5;
  const description = `${
    ownersCount ?? 0
  } Pet Owners across ${suburb} ${stateName} have given ${ratingCount} reviews of Mad Paws Pet Sitters with an average rating of ${roundedRoundedRating}.`;
  const urlWithoutQueryParams = url.split('?')[0];
  const productData = {
    '@type': 'Product',
    brand: 'Mad Paws',
    name: serviceType,
    image: 'https://www.madpaws.com.au/static/logo-dark.png',
    sameAs: 'https://www.madpaws.com.au/',
    description,
    offers: {
      '@type': 'AggregateOffer',
      url: `https://www.madpaws.com.au${urlWithoutQueryParams}`,
      priceCurrency: currencyCode,
      lowPrice: minPrice || 0,
      highPrice: maxPrice || 0,
      offerCount,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: roundedRoundedRating,
      bestRating: 5,
      worstRating: 1,
      reviewCount: ratingCount,
    },
  };

  return getStructuredData(breadcrumbData, productData);
};

type SeoStructuredData = {
  currencyCode?: string;
  maxPrice?: number | null;
  minPrice?: number | null;
  offerCount?: number;
  ownersCount?: number | null;
  ratingCount?: number;
  ratingValue?: number | null;
  serviceType?: string;
  stateName: string;
  suburb: string;
  url: string;
};

type Props = {
  canonicalUrl: string | null;
  isNoIndex?: boolean;
  metaDescription: string;
  pageTitle: string;
  seoStructuredData: SeoStructuredData;
};

export const PageHead = ({
  canonicalUrl,
  isNoIndex = false,
  pageTitle,
  metaDescription,
  seoStructuredData,
}: Props): React.ReactElement => (
  <Head>
    <title>{pageTitle}</title>
    <meta content={metaDescription} name="description" />
    {isNoIndex && <meta content="noindex, follow" name="robots" />}
    {/* see more details about canonical tags */}
    {/* here: https://ahrefs.com/blog/canonical-tags/ */}
    {canonicalUrl && <link href={canonicalUrl} rel="canonical" />}
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/jsx-sort-props, react/no-danger
      dangerouslySetInnerHTML={{ __html: constructStructuredData(seoStructuredData) }}
    />
  </Head>
);
